import { createFileRoute } from '@tanstack/react-router'
import { Link } from '@tanstack/react-router'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/Tabs'
import { Badge } from '../../components/Badge'
import { PlatformIcon } from '../../components/icons/PlatformIcon'
import { MessageHeader } from '../../features/message/MessageHeader'
import { ContextThread } from '../../features/message/ContextThread'
import { CrossGroupTab } from '../../features/message/tabs/CrossGroupTab'
import { useMessageDetail, type MessageDetail } from '../../features/message/hooks/useMessageDetail'
import { AIExtractionChips } from '../../features/message/components/AIExtractionChips'

/* ───── Route Definition ───── */

export const Route = createFileRoute('/_authenticated/messages/$id')({
  component: MessageDetailPage,
})

/* ───── Sentiment helpers ───── */

function sentimentLabel(score: number): { label: string; color: string } {
  if (score > 0.3) return { label: 'Positive', color: 'text-green-600' }
  if (score < -0.3) return { label: 'Negative', color: 'text-red-600' }
  return { label: 'Neutral', color: 'text-[var(--color-text-muted)]' }
}

/* ───── Message Metadata Panel ───── */

function MessageMetadataPanel({ message }: { message: MessageDetail }) {
  const sentiment = message.sentiment_score != null ? sentimentLabel(message.sentiment_score) : null

  return (
    <div
      className="border-b border-[var(--color-border)] bg-[var(--color-surface-secondary)] px-6 py-3"
      data-testid="message-metadata-panel"
    >
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
        {/* Platform */}
        <div className="flex items-center gap-1.5" data-testid="meta-platform">
          <PlatformIcon platform={message.platform} size="xs" />
          <span className="text-[var(--color-text-secondary)] capitalize">{message.platform}</span>
        </div>

        {/* Sender */}
        <div className="flex items-center gap-1.5" data-testid="meta-sender">
          <span className="text-[var(--color-text-muted)]">Sender:</span>
          <Link
            to="/entities/$id"
            params={{ id: message.sender.sqid }}
            className="font-medium text-[var(--color-text-heading)] hover:text-[var(--color-accent)] transition-colors"
          >
            {message.sender.name}
          </Link>
        </div>

        {/* Timestamp */}
        <div className="flex items-center gap-1.5" data-testid="meta-timestamp">
          <span className="text-[var(--color-text-muted)]">Sent:</span>
          <span className="text-[var(--color-text-secondary)]">
            {new Date(message.timestamp).toLocaleString(undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </span>
        </div>

        {/* Message type */}
        {message.message_type && (
          <div className="flex items-center gap-1.5" data-testid="meta-message-type">
            <span className="text-[var(--color-text-muted)]">Type:</span>
            <Badge variant="neutral" size="sm">{message.message_type}</Badge>
          </div>
        )}

        {/* Has media */}
        <div className="flex items-center gap-1.5" data-testid="meta-has-media">
          <span className="text-[var(--color-text-muted)]">Media:</span>
          <span className="text-[var(--color-text-secondary)]">
            {(message.has_media ?? message.media_count > 0)
              ? `${message.media_count} attachment${message.media_count !== 1 ? 's' : ''}`
              : 'None'}
          </span>
        </div>

        {/* Detected language */}
        {message.detected_language && (
          <div className="flex items-center gap-1.5" data-testid="meta-detected-language">
            <span className="text-[var(--color-text-muted)]">Language:</span>
            <Badge variant="neutral" size="sm">{message.detected_language}</Badge>
          </div>
        )}

        {/* Sentiment score */}
        {sentiment && (
          <div className="flex items-center gap-1.5" data-testid="meta-sentiment">
            <span className="text-[var(--color-text-muted)]">Sentiment:</span>
            <span className={`font-medium ${sentiment.color}`}>
              {sentiment.label} ({message.sentiment_score!.toFixed(2)})
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

/* ───── Page Component ───── */

function MessageDetailPage() {
  const { id } = Route.useParams()
  const { message } = useMessageDetail(id)

  // Use sender entity ID for cross-group tab
  const senderEntityId = message?.sender?.sqid

  return (
    <div className="flex flex-col h-full" data-testid="message-detail-page">
      {/* C9: Breadcrumb navigation */}
      <nav className="flex items-center gap-1.5 px-6 py-2 text-xs text-[var(--color-text-secondary)] bg-[var(--color-surface)]" aria-label="Breadcrumb" data-testid="breadcrumb">
        <Link to="/dashboard" className="hover:text-[var(--color-text-primary)] transition-colors">Home</Link>
        <span aria-hidden="true">/</span>
        <Link to="/search" className="hover:text-[var(--color-text-primary)] transition-colors">Messages</Link>
        <span aria-hidden="true">/</span>
        <span className="text-[var(--color-text-primary)] font-medium truncate max-w-[200px]">
          {message?.sender?.name ? `${message.sender.name} - Message` : id}
        </span>
      </nav>

      {/* Header */}
      <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)]">
        <MessageHeader messageId={id} />
      </div>

      {/* Metadata panel */}
      {message && <MessageMetadataPanel message={message} />}

      {/* ING-45: AI extraction chips (entities, locations, objects) */}
      <div className="border-b border-[var(--color-border)] px-6 py-2">
        <AIExtractionChips messageId={id} />
      </div>

      {/* Tabbed content: Context + Cross-Group */}
      <div className="flex-1 overflow-hidden">
        <Tabs defaultValue="context" className="flex flex-col h-full">
          <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-6">
            <TabsList>
              <TabsTrigger value="context">Context Thread</TabsTrigger>
              <TabsTrigger value="cross-group" disabled={!senderEntityId}>
                Cross-Group
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="context" className="flex-1 overflow-hidden">
            <ContextThread messageId={id} />
          </TabsContent>

          <TabsContent value="cross-group" className="flex-1 overflow-hidden">
            {senderEntityId && (
              <CrossGroupTab entityId={senderEntityId} />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
